const DEFAULT_THEME_COLOR = "#036661";

const DEFAULT_ICONS = {
  icon192: "/icons/apointa-192.png",
  icon512: "/icons/apointa-512.png",
  maskable512: "/icons/apointa-maskable-512.png",
  apple180: "/icons/apointa-180.png"
};

const CLOUDINARY_UPLOAD = /^https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\//;

// The manifest blob URL currently on the page. Never revoked while it is the live
// one: Chrome re-reads the manifest when it decides to offer an install.
let manifestObjectUrl = null;

/** A Cloudinary delivery URL with `transform` applied, or null for any other URL. */
export function cloudinaryVariant(url, transform) {
  if (!url || !CLOUDINARY_UPLOAD.test(url)) return null;
  return url.replace("/image/upload/", `/image/upload/${transform}/`);
}

/**
 * Home-screen icons for a club: its logo padded onto a white square when the logo
 * lives on Cloudinary (which can resize it on the fly), else Apointa's own. The
 * maskable icon keeps the logo inside the central 80% that launchers never crop.
 */
export function appIcons(logoUrl, origin = "") {
  const padded = (size) => cloudinaryVariant(logoUrl, `c_pad,w_${size},h_${size},b_white,f_png`);
  const maskable = cloudinaryVariant(logoUrl, "c_pad,w_410,h_410,b_white/c_pad,w_512,h_512,b_white,f_png");
  if (!maskable) {
    return Object.fromEntries(Object.entries(DEFAULT_ICONS).map(([key, path]) => [key, `${origin}${path}`]));
  }
  return { icon192: padded(192), icon512: padded(512), maskable512: maskable, apple180: padded(180) };
}

/**
 * The academy's own web-app manifest: installed from /tennis360/, the phone gets
 * "Tennis 360" with its logo and colour, and opens straight into that club.
 *
 * Every URL is absolute — the manifest is served from a blob: URL, against which
 * relative URLs don't resolve. Scope is the whole origin rather than the club's
 * path: the payment return (/checkout/...) and confirmation pages are slug-less,
 * and leaving the app's scope mid-payment drops an installed app into a browser
 * sheet. `id` is what keeps two academies installed from this origin apart.
 */
export function buildManifest({ slug, name, color, logoUrl, origin }) {
  const icons = appIcons(logoUrl, origin);
  const appName = (name || "").trim() || "Apointa Booking";
  return {
    id: `${origin}/${slug}/`,
    name: appName,
    short_name: appName,
    start_url: `${origin}/${slug}/?src=pwa`,
    scope: `${origin}/`,
    display: "standalone",
    theme_color: color || DEFAULT_THEME_COLOR,
    background_color: "#ffffff",
    icons: [
      { src: icons.icon192, sizes: "192x192", type: "image/png", purpose: "any" },
      { src: icons.icon512, sizes: "512x512", type: "image/png", purpose: "any" },
      { src: icons.maskable512, sizes: "512x512", type: "image/png", purpose: "maskable" }
    ]
  };
}

function headElement(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(name, content) {
  const meta = headElement(`meta[name="${name}"]`, () => {
    const m = document.createElement("meta");
    m.name = name;
    return m;
  });
  meta.content = content;
}

/**
 * Install metadata for one academy: the runtime manifest (Chrome / Android) and
 * the Apple tags. iOS Safari does not reliably read a blob: manifest when adding
 * to the Home Screen, so the Apple tags carry the name, icon and standalone mode
 * on their own — and standalone is what unlocks web push on iOS 16.4+. If a real
 * device shows Safari ignoring the blob entirely, the fallback is the API serving
 * the manifest from a URL (FEATURE_WIDGET_PWA_WEB_PUSH.md §5.1).
 */
function applyInstallMetadata({ slug, name, color, logoUrl }) {
  const origin = window.location.origin;
  const manifest = buildManifest({ slug, name, color, logoUrl, origin });

  const previous = manifestObjectUrl;
  manifestObjectUrl = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" }));
  const link = headElement('link[rel="manifest"]', () => {
    const l = document.createElement("link");
    l.rel = "manifest";
    return l;
  });
  link.href = manifestObjectUrl;
  if (previous) URL.revokeObjectURL(previous);

  setMeta("theme-color", manifest.theme_color);
  setMeta("apple-mobile-web-app-capable", "yes");
  setMeta("mobile-web-app-capable", "yes");
  setMeta("apple-mobile-web-app-title", manifest.name);
  setMeta("apple-mobile-web-app-status-bar-style", "default");

  const touchIcon = headElement('link[rel="apple-touch-icon"]', () => {
    const l = document.createElement("link");
    l.rel = "apple-touch-icon";
    return l;
  });
  touchIcon.href = appIcons(logoUrl, origin).apple180;
}

/**
 * Tab title and favicon for any page that knows its club. With a `slug` (the
 * widget proper, after init) it also brands the installable app; slug-less pages
 * (checkout landings) keep the static fallback manifest.
 */
export function applyBranding({ name, logoUrl, slug, color } = {}) {
  if (typeof document === "undefined") return;

  const trimmed = (name || "").trim();
  if (trimmed) {
    document.title = `${trimmed} · Apointa Booking`;
  }

  let icon = document.querySelector('link[rel="icon"]');
  if (!icon) {
    icon = document.createElement("link");
    icon.rel = "icon";
    document.head.appendChild(icon);
  }

  icon.type = "image/png";
  icon.href = logoUrl || "/favicon.png";

  if (slug) applyInstallMetadata({ slug, name: trimmed, color, logoUrl });
}
